/**
 * AnnouncementRenderer — bold fighting-game-style text popups.
 *
 * Two modes:
 * - Screen-space: fixed position in uiContainer (phase title cards)
 * - World-space: positioned in worldContainer, drifts upward (hit impact, KO)
 *
 * Animation: punch-in (scale overshoot → 1.0) → hold → fade out.
 * Font: "Black Ops One", all-caps, black stroke. Fighting game bold.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { ANNOUNCE, WORLD, Z } from '../constants';

interface ActiveAnnouncement {
  text: Text;
  mode: 'screen' | 'world';
  // Animation state
  phase: 'delay' | 'punch' | 'hold' | 'fade';
  phaseTimer: number;
  delayDuration: number;
  // Timing
  holdDuration: number;
  fadeDuration: number;
  // World-space drift
  driftVy: number;
  baseX: number;
  baseY: number;
}

const MAX_ACTIVE = 16;

export class AnnouncementRenderer {
  private worldContainer: Container;
  private uiContainer: Container;
  private active: ActiveAnnouncement[] = [];

  constructor(worldContainer: Container, uiContainer: Container) {
    this.worldContainer = worldContainer;
    this.uiContainer = uiContainer;
  }

  /**
   * Show screen-space announcement (fixed position, phase title cards).
   * y is fraction of canvas height (default: ANNOUNCE.SCREEN_CENTER_Y).
   */
  showScreenText(
    label: string,
    opts: { color: number; fontSize: number; hold?: number; y?: number; x?: number; delay?: number },
  ): void {
    if (this.active.length >= MAX_ACTIVE) this.removeOldest();

    const style = new TextStyle({
      fontFamily: '"Black Ops One", sans-serif',
      fontSize: opts.fontSize,
      fill: opts.color,
      stroke: { color: 0x000000, width: 4 },
      align: 'center',
    });

    const text = new Text({ text: label, style });
    text.anchor.set(0.5, 0.5);
    text.x = opts.x != null ? WORLD.CANVAS_WIDTH * opts.x : WORLD.CANVAS_WIDTH / 2;
    const yFrac = opts.y ?? ANNOUNCE.SCREEN_CENTER_Y;
    text.y = WORLD.CANVAS_HEIGHT * yFrac;
    text.scale.set(ANNOUNCE.START_SCALE);
    text.zIndex = Z.FLASH + 10; // above flash overlay

    const hasDelay = (opts.delay ?? 0) > 0;
    if (hasDelay) text.alpha = 0; // hidden during delay
    this.uiContainer.addChild(text);

    this.active.push({
      text,
      mode: 'screen',
      phase: hasDelay ? 'delay' : 'punch',
      phaseTimer: 0,
      delayDuration: opts.delay ?? 0,
      holdDuration: opts.hold ?? ANNOUNCE.HOLD_DURATION,
      fadeDuration: ANNOUNCE.FADE_DURATION,
      driftVy: 0,
      baseX: text.x,
      baseY: text.y,
    });
  }

  /**
   * Show world-space announcement (moves with camera, drifts upward).
   * Used for hit impact text, KO text.
   */
  showWorldText(
    label: string,
    x: number, y: number,
    opts: { color: number; fontSize: number; hold?: number },
  ): void {
    if (this.active.length >= MAX_ACTIVE) this.removeOldest();

    const style = new TextStyle({
      fontFamily: '"Black Ops One", sans-serif',
      fontSize: opts.fontSize,
      fill: opts.color,
      stroke: { color: 0x000000, width: 4 },
      align: 'center',
    });

    const text = new Text({ text: label, style });
    text.anchor.set(0.5, 0.5);
    text.x = x;
    text.y = y;
    text.scale.set(ANNOUNCE.START_SCALE);
    text.zIndex = Z.DI_OVERLAY + 5; // above tanks, below flash
    this.worldContainer.addChild(text);

    this.active.push({
      text,
      mode: 'world',
      phase: 'punch',
      phaseTimer: 0,
      delayDuration: 0,
      holdDuration: opts.hold ?? ANNOUNCE.HOLD_DURATION,
      fadeDuration: ANNOUNCE.FADE_DURATION,
      driftVy: ANNOUNCE.DRIFT_VY,
      baseX: x,
      baseY: y,
    });
  }

  update(dt: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i];
      a.phaseTimer += dt;

      switch (a.phase) {
        case 'delay': {
          if (a.phaseTimer >= a.delayDuration) {
            a.phase = 'punch';
            a.phaseTimer = 0;
            a.text.alpha = 1;
          }
          break;
        }
        case 'punch': {
          // Ease-out-back: overshoot → 1.0
          const t = Math.min(1, a.phaseTimer / ANNOUNCE.PUNCH_DURATION);
          const eased = easeOutBack(t);
          const scale = ANNOUNCE.START_SCALE + (1.0 - ANNOUNCE.START_SCALE) * eased;
          a.text.scale.set(scale);
          a.text.alpha = 1;
          if (t >= 1) {
            a.phase = 'hold';
            a.phaseTimer = 0;
          }
          break;
        }
        case 'hold': {
          a.text.scale.set(1);
          a.text.alpha = 1;
          if (a.phaseTimer >= a.holdDuration) {
            a.phase = 'fade';
            a.phaseTimer = 0;
          }
          break;
        }
        case 'fade': {
          const t = Math.min(1, a.phaseTimer / a.fadeDuration);
          a.text.alpha = 1 - t;
          // World-space drift upward during fade
          if (a.mode === 'world') {
            a.text.y += a.driftVy * dt;
          }
          if (t >= 1) {
            this.removeAt(i);
          }
          break;
        }
      }
    }
  }

  private removeAt(index: number): void {
    const a = this.active[index];
    a.text.destroy();
    this.active.splice(index, 1);
  }

  private removeOldest(): void {
    if (this.active.length > 0) {
      this.removeAt(0);
    }
  }

  destroy(): void {
    for (const a of this.active) {
      a.text.destroy();
    }
    this.active.length = 0;
  }
}

/** Ease-out-back curve: overshoots slightly then settles. */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}
